import {
  CreditCheckResultEnum,
  InstallationOptionsEnum,
} from '@updater/consumer-graph';
import { useGetEstimatedActivationDate } from 'flows/tvi/hooks/use-get-estimated-activation-date';
import { useReservation } from '@updater/ui-widgets';
import type { Address, CreditCard, PiiTokenizedData } from 'flows/tvi/types';
import { useEffect } from 'react';
import { useUserData } from 'flows/core/hooks/use-profile';
import { createFormStore } from '../common';
import { useXfinityCreditCheck } from 'flows/tvi/hooks/use-xfinity-credit-check';
import { SMS_OPT_IN_VALUE } from 'flows/tvi/components/review/utils/constants';
import { useBuyFlowContext } from 'flows/tvi/generic-buyflow/contexts/BuyFlowContext';
import type { getUserInfo_getUser_user_currentMove_fromAddress as FromAddress } from 'types/generated/getUserInfo';

export type XFinityFormData = {
  flexDevices?: string;
  internetModemChoice?:
    | 'internetModemOwned'
    | 'xfinityGateway'
    | 'internetModemOwned';
  asyncCreditCheck: {
    creditCheckResult?: CreditCheckResultEnum | 'not_started';
    dobPiiToken?: string;
    ssnPiiToken?: string;
  };
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  smsOptIn: 'sms'[];
  priorAddress?: Address;
  creditCard?: Partial<CreditCard>;
  installationType?: InstallationOptionsEnum;
  installationDate?: string;
  installationTimeWindow?:
    | '8am to 10am'
    | '10am to 12pm'
    | '1pm to 3pm'
    | '3pm to 5pm';
  // TODO: Remove after dob_ssn is productionized
  dob: string | PiiTokenizedData;
  // TODO: Remove after dob_ssn is productionized
  ssn?: PiiTokenizedData;
  serviceTransferDates?: {
    dateOrderIsValid?: boolean;
    reconnectDate?: string;
    disconnectDate?: string;
  };
  tvEquipmentChoice?: 'x1-tv-box' | 'customer-owned';
  paymentMethod?: 'autopay' | 'deposit' | 'authorize-credit-check';
};

export enum XFinityPages {
  MODEMS = 'modems',
  PAYMENT = 'payment-method',
  OPTION = 'payment-option',
  INFO = 'payment-info',
  REVIEW = 'buy/review',
  DOB = 'dob',
  SUBMISSION = 'submission',
  ERROR = 'error',
  SCHEDULE_CONNECTION = 'schedule-connection',
}

export const omitKeysForLogging = [
  'dob',
  'ssn',
  'creditCard',
  'dobPiiToken',
  'ssnPiiToken',
];

export const useStore = createFormStore<XFinityFormData, XFinityPages>({
  provider: 'xfinity',
  initialPageRoute: XFinityPages.MODEMS,
  initialFormData: {},
});

export const getXFinitiyFormState = () => useStore.getState();

// TODO: add TV equipment & in transform props
export const useXFinityFormStore = (...args: Parameters<typeof useStore>) => {
  const status = useStore((state) => state.status);
  const initializeFormData = useStore((state) => state.initializeFormData);
  const resetFormData = useStore((state) => state.resetFormData);
  const lastIdentifier = useStore((state) => state.lastIdentifier);
  const { loading: userDataLoading, user } = useUserData();
  const currentMoveId = user?.currentMove?.id;
  const reservationData = useReservation({
    moveId: currentMoveId ?? '',
    skip: !currentMoveId,
  });

  const {
    creditCheckResult,
    ssnPiiToken,
    dobPiiToken,
    loading: xfinityCreditCheckLoading,
  } = useXfinityCreditCheck();

  const activationDefaults = useGetEstimatedActivationDate();

  // TODO: Refactor so that we don't need to use two different reservation objects in this file
  const { isGuestMode, identifier, customerInfo, installAddress, reservation } =
    useBuyFlowContext();

  const hasHydrated = useStore.persist?.hasHydrated();

  useEffect(() => {
    if (
      hasHydrated &&
      identifier &&
      lastIdentifier &&
      identifier !== lastIdentifier
    ) {
      resetFormData();
    }
  }, [hasHydrated, identifier, lastIdentifier, resetFormData]);

  useEffect(() => {
    if (
      hasHydrated &&
      status !== 'loaded' &&
      !userDataLoading &&
      !xfinityCreditCheckLoading &&
      !reservationData?.loading &&
      !activationDefaults.loading
    ) {
      const installationTypeFromReservation =
        reservation?.offer?.options?.installationOptions?.type ??
        InstallationOptionsEnum.self;

      const isTransferReservation =
        reservationData?.reservationInfo?.preferences?.flow === 'TRANSFER';

      const installationType = !isTransferReservation
        ? installationTypeFromReservation
        : undefined;

      const nonTransferDefaultPaymentMethod =
        creditCheckResult === CreditCheckResultEnum.passed
          ? 'authorize-credit-check'
          : undefined; // user will choose payment method later in the flow

      // Only if TV bundle provide equipment value
      const defaultTvEquipmentChoice: XFinityFormData['tvEquipmentChoice'] =
        reservationData?.offer?.products?.tv ? 'customer-owned' : undefined;

      // Guest mode: Get customer info from context
      // Auth mode: Get customer info from user object
      let customerFirstName: string | undefined;
      let customerLastName: string | undefined;
      let customerEmail: string | undefined;
      let customerPhone: string | undefined;
      let customerAddress: Address | FromAddress | undefined;

      if (isGuestMode && customerInfo) {
        customerFirstName = customerInfo.firstName;
        customerLastName = customerInfo.lastName;
        customerEmail = customerInfo.email;
        customerPhone = customerInfo.phone;
        customerAddress = installAddress
          ? {
              street: installAddress.street,
              city: installAddress.city,
              state: installAddress.state,
              postalCode: installAddress.postalCode,
            }
          : undefined;
      } else {
        // Use nullish coalescing to filter out null values
        customerFirstName = user?.firstName ?? undefined;
        customerLastName = user?.lastName ?? undefined;
        customerEmail = user?.email ?? undefined;
        customerPhone = user?.phone ?? undefined;
        customerAddress = user?.currentMove?.fromAddress;
      }

      const initialData: Partial<XFinityFormData> = {
        internetModemChoice: !isTransferReservation
          ? 'xfinityGateway'
          : undefined,
        asyncCreditCheck: {
          creditCheckResult: creditCheckResult ?? 'not_started',
          ssnPiiToken,
          dobPiiToken,
        },
        paymentMethod: !isTransferReservation
          ? nonTransferDefaultPaymentMethod
          : undefined,
        // @ts-expect-error FIXME: TS2322 - strict mode migration
        installationType,
        installationDate: activationDefaults.estimatedDate ?? '',
        // @ts-expect-error FIXME: TS2322 - strict mode migration
        installationTimeWindow:
          installationType === InstallationOptionsEnum.self
            ? undefined
            : activationDefaults.defaultTimeWindow,
        tvEquipmentChoice: defaultTvEquipmentChoice,
        // Removing flexDevice for now until its pricing is implemented
        // Reference story https://updater.atlassian.net/browse/UPA-6
        // flexDevices: 'one',
        firstName: customerFirstName,
        lastName: customerLastName,
        email: customerEmail,
        phone: customerPhone,
        smsOptIn: [SMS_OPT_IN_VALUE],
        creditCard: !isTransferReservation
          ? {
              firstName: customerFirstName,
              lastName: customerLastName,
              address: customerAddress,
            }
          : undefined,
        priorAddress:
          !isTransferReservation && user?.currentMove?.fromAddress
            ? <Address>{
                street: user.currentMove.fromAddress.street ?? undefined,
                unit: user.currentMove.fromAddress.unit ?? '',
                city: user.currentMove.fromAddress.city ?? undefined,
                state: user.currentMove.fromAddress.state ?? undefined,
                postalCode:
                  user.currentMove.fromAddress.postalCode ?? undefined,
              }
            : undefined,
      };

      initializeFormData({
        formData: initialData,
        offerName: reservationData?.offer?.name,
        identifier,
      });
    }
  }, [
    status,
    user,
    userDataLoading,
    hasHydrated,
    initializeFormData,
    reservationData?.offer?.products?.tv,
    reservationData?.loading,
    reservationData?.offer?.options?.installationOptions?.type,
    reservationData?.offer?.name,
    reservationData?.reservationInfo?.preferences?.flow,
    activationDefaults,
    xfinityCreditCheckLoading,
    creditCheckResult,
    ssnPiiToken,
    dobPiiToken,
    isGuestMode,
    identifier,
  ]);

  return useStore(...args);
};
